import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { label: 'Cloud Solutions', path: '/services' },
      { label: 'Cybersecurity', path: '/services' },
      { label: 'Full-Stack Development', path: '/services' },
      { label: 'UI/UX Design', path: '/services' },
      { label: 'Data Analytics', path: '/services' },
      { label: 'Managed IT Support', path: '/services' }
    ],
    company: [
      { label: 'About Us', path: '/about' },
      { label: 'Our Services', path: '/services' },
      { label: 'Contact', path: '/contact' },
      { label: 'Get Started', path: '/application' }
    ],
    resources: [
      { label: 'Documentation', path: '#' },
      { label: 'API Reference', path: '#' },
      { label: 'Support Center', path: '#' },
      { label: 'System Status', path: '#' }
    ]
  };

  const socialLinks = [
    { name: 'LinkedIn', icon: 'Linkedin', url: 'https://linkedin.com/company/daraja-labs' },
    { name: 'Twitter', icon: 'Twitter', url: 'https://twitter.com/darajalabs' },
    { name: 'GitHub', icon: 'Github', url: 'https://github.com/daraja-labs' },
    { name: 'Instagram', icon: 'Instagram', url: 'https://instagram.com/darajalabs' }
  ];

  const contactInfo = [
    { icon: 'Mail', text: 'Info@daraja.com', link: 'mailto:info@daraja.com' },
    { icon: 'Phone', text: '+1 240-598-5442', link: 'tel:+1 240-598-5442' },
    { icon: 'Phone', text: '+254 707 366350', link: 'tel:+254 707 366350' },
    { icon: 'Phone', text: '+231 770066992 WhatsApp Only', link: 'tel:+231 770066992' },
    { icon: 'MapPin', text: '8225 Shooting Star Lane, Laurel, MD 20724', link: '#' }
  ];

  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                {/* Logo */}
                <Link to="/home" className="flex items-center space-x-2 mb-6">
                  <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
                    <span className="text-accent-foreground font-bold text-xl">D</span>
                  </div>
                  <span className="text-foreground font-semibold text-xl">
                    DARAJA.LABS
                  </span>
                </Link>

                <p className="text-text-secondary mb-6 leading-relaxed">
                  Bridging the gap between your business and digital transformation with innovative IT solutions.
                </p>

                {/* Contact Info */}
                <div className="space-y-3">
                  {contactInfo.map((item, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <a
                        href={item.link}
                        className="flex items-center text-text-secondary hover:text-accent transition-colors duration-200 group"
                      >
                        <Icon 
                          name={item.icon} 
                          size={16} 
                          className="mr-3 group-hover:text-accent transition-colors duration-200" 
                        />
                        {item.text}
                      </a>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Services Links */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-foreground font-semibold text-lg mb-6">Services</h3>
                <ul className="space-y-3">
                  {footerLinks.services.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.path}
                        className="text-text-secondary hover:text-accent transition-colors duration-200 hover:translate-x-1 inline-block"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Company Links */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-foreground font-semibold text-lg mb-6">Company</h3>
                <ul className="space-y-3">
                  {footerLinks.company.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.path}
                        className="text-text-secondary hover:text-accent transition-colors duration-200 hover:translate-x-1 inline-block"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Resources & Newsletter */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h3 className="text-foreground font-semibold text-lg mb-6">Resources</h3>
                <ul className="space-y-3 mb-8">
                  {footerLinks.resources.map((link, index) => (
                    <li key={index}>
                      <a
                        href={link.path}
                        className="text-text-secondary hover:text-accent transition-colors duration-200 hover:translate-x-1 inline-block"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>

                {/* Newsletter Signup */}
                <div>
                  <h4 className="text-foreground font-medium mb-4">Stay Updated</h4>
                  <div className="flex">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="flex-1 bg-background border border-border rounded-l-lg px-4 py-2 text-foreground placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    />
                    <button className="bg-accent text-accent-foreground px-4 py-2 rounded-r-lg hover:bg-accent/90 transition-colors duration-200">
                      <Icon name="Send" size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-border py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-text-secondary text-sm"
            >
              © {currentYear} DARAJA.LABS. All rights reserved.
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="flex space-x-4"
            >
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center text-text-secondary hover:text-accent hover:bg-accent/20 transition-all duration-200"
                  aria-label={social.name}
                >
                  <Icon name={social.icon} size={18} />
                </motion.a>
              ))}
            </motion.div>

            {/* Legal Links */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="flex space-x-6 text-sm"
            >
              <Link to="/src/pages/additional_pages/policy.html" className="text-text-secondary hover:text-accent transition-colors duration-200">
                FAQs
              </Link>
              <Link to="/terms" className="text-text-secondary hover:text-accent transition-colors duration-200">
                Terms of Service
              </Link>
              <Link to="/privacy" className="text-text-secondary hover:text-accent transition-colors duration-200">
                Privacy Policy
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;